// src/app/shared/services/generic-table-handler.service.ts

import { BehaviorSubject, combineLatest, Observable, Subject, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { BaseService } from '../services/data.service';
import { TableConfig } from '../table-generic/table-generic.component';
import { PageEvent } from '@angular/material/paginator';

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

export abstract class GenericTableHandlerService<T = any> {
  protected endpoint = '';
  protected dialogComponent: any = null;

  tableConfig$ = new BehaviorSubject<TableConfig | null>(null);
  data$ = new BehaviorSubject<T[]>([]);
  loading$ = new BehaviorSubject<boolean>(false);
  error$ = new BehaviorSubject<string | null>(null);
  selectedItems$ = new BehaviorSubject<T[]>([]);
  searchTerm: string = '';

  currentPage$ = new BehaviorSubject<number>(0);
  totalItems$ = new BehaviorSubject<number>(0);
  pageSize$ = new BehaviorSubject<number>(10);
  currentSort$ = new BehaviorSubject<SortConfig>({ key: '', direction: 'asc' });
  filters$ = new BehaviorSubject<Record<string, any>>({});

  tableState$ = combineLatest([
    this.loading$,
    this.error$,
    this.data$
  ]).pipe(
    map(([loading, error, data]) => ({ loading, error, empty: data.length === 0 }))
  );

  constructor(
    protected baseService: BaseService,
    protected dialog: MatDialog,
    protected toastr: ToastrService
  ) {}

  initialize(config: TableConfig, dialogComponent: any, endpoint: string): void {
    this.dialogComponent = dialogComponent;
    this.endpoint = endpoint;
    this.tableConfig$.next({ ...this.getDefaultConfig(), ...config });
  }

  protected getDefaultConfig(): TableConfig {
    return {
      pageSize: 10,
      pageSizeOptions: [5, 10, 25, 100],
      noDataMessage: 'No hay datos disponibles',
      columns: [],
      actions: []
    };
  }

  loadData(extraParams: Record<string, any> = {}): void {
    this.loading$.next(true);
    this.error$.next(null);
    const query = this.buildQueryParams(extraParams);
    this.baseService.get(`${this.endpoint}${query}`)
      .pipe(
        tap(response => this.handleResponse(response)),
        catchError(error => this.handleError(error)),
        finalize(() => this.loading$.next(false))
      )
      .subscribe();
  }

  protected handleResponse(response: any): void {
    if (Array.isArray(response)) {
      // Caso especial: el backend responde un array plano
      this.data$.next(response);
      this.totalItems$.next(response.length);
    } else {
      // Caso normal con paginación
      this.data$.next(response.data || response.content || []);
      this.totalItems$.next(response.total || response.totalElements || response.length || 0);
    }
  }
  

  protected buildQueryParams(extraParams: Record<string, any> = {}): string {
    const params = new URLSearchParams();
    const page = this.currentPage$.value;
    const size = this.pageSize$.value;
    const sort = this.currentSort$.value;
    const filters = this.filters$.value;

    if (page >= 0) params.append('page', (page + 1).toString());
    if (size > 0) params.append('size', size.toString());
    if (sort.key) params.append('sort', `${sort.key},${sort.direction}`);

    for (const [key, value] of Object.entries(filters)) {
      if (value != null && value !== '') params.append(key, value);
    }

    for (const [key, value] of Object.entries(extraParams)) {
      if (value != null) params.append(key, value);
    }

    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }

  protected handleError(error: any): Observable<never> {
    const message = error?.error?.message || error?.message || 'Error al cargar los datos';
    this.error$.next(message);
    this.toastr.error(message);
    return throwError(() => new Error(message));
  }

  onPageChange(event: PageEvent): void {
    this.currentPage$.next(event.pageIndex);
    this.pageSize$.next(event.pageSize);
    this.loadData();
  }

  onSortChange(sort: { active: string; direction: string }): void {
    this.currentSort$.next({
      key: sort.active,
      direction: sort.direction as 'asc' | 'desc'
    });
    this.loadData();
  }

  deleteItem(id: any): Observable<any> {
    this.loading$.next(true);
    let url;
    if(this.endpoint == '' || this.endpoint == null){
      url = `${id}`;
    }else{
      url = `/${this.endpoint}/${id}`;  
    }
    return this.baseService.delete(url).pipe(
      tap(() => {
        this.toastr.success('Eliminado exitosamente');
        this.loadData();
      }),
      catchError(error => this.handleError(error)),
      finalize(() => this.loading$.next(false))
    );
  }
  private dataUpdatedSubject = new Subject<void>();
  dataUpdated$ = this.dataUpdatedSubject.asObservable();

  // Método para notificar actualizaciones
  notifyDataUpdate(): void {
    this.dataUpdatedSubject.next();
    this.loadData();
  }
}
